<script setup lang="ts">
  import type { BestBuyBannerStoryblok, LinkStoryblok } from '@/storyblok/component-types-sb'
  import type { ISbStoryData } from '@storyblok/js'
  import { SHOW_ALL_ID } from '@/components/best-buy-banner/constants'
  import { useGlobalSettingsStore } from '@/stores/GlobalSettings.store'
  import useIndexPageDataLayer from '@/composables/data-layers/useIndexPageDataLayer'

  const globalSettingsStore = useGlobalSettingsStore()
  const { ripStatus } = storeToRefs(globalSettingsStore)
  const disabled = computed(() => ripStatus.value?.flt)

  const props = defineProps<{ blok: BestBuyBannerStoryblok }>()

  const { locale } = useI18n()

  const { data } = await useFetch('/api/banners/main', {
    query: {
      language: locale.value
    },
    lazy: true,
    deep: false,
    transform: (bannerData) => ({
      banners: new Map(bannerData.data?.banners),
      originCountries: bannerData.data?.originCountries,
      originCities: new Map(bannerData.data?.originCities)
    })
  })

  const localeStore = useLocaleStore()
  const selectedOriginCountry = ref({ id: localeStore.selectedCountry, label: '' })

  const { isDesktop } = useBTBreakpoints()
  const MAX_BANNERS_TO_SHOW = 4
  const bannersToShow = computed(() => (isDesktop.value ? MAX_BANNERS_TO_SHOW : 3))
  const showSlider = computed(
    () => activeBanners.value.length > MAX_BANNERS_TO_SHOW && props.blok.showSlider && isDesktop.value
  )
  const shouldShowBestBuyBanners = computed(() => !disabled.value && activeBanners.value.length > 0)

  const activeBanners = computed(() => {
    if (!data.value?.banners?.size) {
      return []
    }

    const activeCountryBanners = data.value.banners.get(selectedOriginCountry.value?.id)

    let result = []
    if (selectedOriginCity.value?.id === SHOW_ALL_ID) {
      result = activeCountryBanners ?? []
    } else {
      result = activeCountryBanners?.filter((banner) => banner.origin === selectedOriginCity.value.id) ?? []
    }

    return props.blok.showSlider && isDesktop.value ? result : [...result].splice(0, bannersToShow.value)
  })

  const selectedOriginCity = ref({ id: SHOW_ALL_ID, label: '' })

  const destinationsDataLinkStory =
    await useFetchDataFromStoryblok<ISbStoryData<LinkStoryblok>>('/data/links/destinations')
  const runtimeConfig = useRuntimeConfig()
  const allDestinationsUrl = computed(
    () => `${destinationsDataLinkStory.value?.content?.href.url ?? runtimeConfig.public.siteUrl}`
  )

  const { pushBestBuyBannerDataLayer } = useIndexPageDataLayer()
  async function onSeeAllDestinationsClick() {
    pushBestBuyBannerDataLayer({
      event_category: 'see_all_destinations',
      event_action: 'click'
    })

    await navigateTo(allDestinationsUrl.value, {
      external: true
    })
  }

  const titleColorClass = ['text-bt-blue-dark', { 'md:text-white': !props.blok.darkTheme }]
  const linkColorClass = ['text-bt-blue-dark', { 'text-bt-blue-dark md:text-bt-green': !props.blok.darkTheme }]
</script>
<template>
  <div
    v-if="shouldShowBestBuyBanners"
    class="mb-10 md:mb-18 lg:mb-10 xl:mb-18"
  >
    <div class="mb-6 mt-6 md:mb-10 lg:mb-8">
      <div
        class="flex justify-between"
        :class="props.blok.darkTheme ? 'items-end' : 'items-start'"
      >
        <div v-if="blok.title">
          <h2
            class="mb-6 md:mb-4"
            :class="titleColorClass"
          >
            {{ blok.title }}
          </h2>
          <BestBuyBannerFilter
            v-if="data?.originCountries?.length"
            v-model:origin-city="selectedOriginCity"
            v-model:origin-country="selectedOriginCountry"
            :origin-countries="data?.originCountries"
            :origin-cities="data?.originCities"
            :dark-theme="blok.darkTheme"
          />
        </div>
        <div class="hidden items-center gap-6 md:flex">
          <NuxtLink
            class="block decoration-1 underline-offset-8 text-h5"
            :class="linkColorClass"
            :to="allDestinationsUrl"
            @click.prevent="onSeeAllDestinationsClick"
          >
            {{ $t('destinations.seeAll') }}
          </NuxtLink>
        </div>
      </div>
    </div>
    <BestBuyBannerSlider
      v-if="showSlider"
      :slides="activeBanners"
      :dark-theme="blok.darkTheme"
    />
    <TransitionGroup
      v-if="!showSlider"
      name="list"
      tag="div"
      class="relative grid w-full grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4"
    >
      <LazyBestBuyBannerCard
        v-for="banner in activeBanners"
        :key="`${banner.origin}-${banner.dest}`"
        :data="banner"
      />
    </TransitionGroup>
    <NuxtLink
      :to="allDestinationsUrl"
      class="mt-4 block w-full rounded-sm border border-bt-blue-dark bg-white py-3 text-center text-bt-blue-dark no-underline text-subtitle-1 md:hidden md:px-6"
      @click.prevent="onSeeAllDestinationsClick"
    >
      {{ $t('destinations.seeAll') }}
    </NuxtLink>
  </div>
</template>
<style lang="scss" scoped>
  .list {
    &-move,
    &-enter-active,
    &-leave-active {
      transition: all 0.3s ease;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
    }

    &-leave-active {
      display: none;
    }
  }
</style>
